/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import {
  toChildArray,
  type ComponentChildren,
  type ComponentProps,
  type Ref,
  type JSX
} from 'preact';
import { forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'preact/compat';

import type { FocusableHandle } from '#hooks/UNSAFE_useFocusableTextField';
import { useCollectionFocusRing } from '#hooks/PRIVATE_useCollectionFocusRing';
import { useTranslationBundle } from '#hooks/UNSAFE_useTranslationBundle';
import type { BundleType } from '#resources/nls/bundle';
import type { UnpackSignals } from '@oracle/oraclejet-internal-utilities/attributeUtils';
import { Label } from '#UNSAFE_Label';
import {
  InlineHelp,
  InlineUserAssistance,
  InlineUserAssistanceContainer,
  type UserAssistanceDensityType
} from '#UNSAFE_UserAssistance';
import { HiddenAccessible } from '#UNSAFE_HiddenAccessible';
import { topUAContainerStyles } from '#PRIVATE_CheckboxRadioField/CheckboxRadioFieldStyles.css';
import { CheckboxRadioContext } from '#PRIVATE_CheckboxRadioField/CheckboxRadioContext';
import { useCheckboxRadioField } from '#PRIVATE_CheckboxRadioField/useCheckboxRadioField';
import { layoutSpanStyles, type LayoutColumnSpan } from '#utils/UNSAFE_styles/Layout';
import { type TestIdProps, useTestId } from '#hooks/UNSAFE_useTestId';
import { Grid } from '#UNSAFE_Grid';
import { mergeRefs } from '#utils/PRIVATE_refUtils';
import { useRichSelectionGrid } from './useRichSelectionGrid';
import { View } from '#UNSAFE_View';
import type { Size } from '#utils/UNSAFE_size';
import { RichSelectionStartContext } from './RichSelectionStartContext';
import { RichLabelValueLayout } from './RichLabelValueLayout';

type InlineUserAssistanceProps = ComponentProps<typeof InlineUserAssistance>;

type AriaProps = Pick<UnpackSignals<JSX.AriaAttributes>, 'aria-describedby'>;

type Props = TestIdProps &
  AriaProps & {
    /**
     * Text to provide guidance to help the user understand what data to enter.
     */
    assistiveText?: InlineUserAssistanceProps['assistiveText'];
    /**
     * baseId, used to generate other element IDs.
     */
    baseId: string;
    /**
     * <RichSelectionItems />
     */
    children: ComponentChildren;
    /**
     * Specifies how many columns to span in a FormLayout with direction === 'row'
     */
    columnSpan?: LayoutColumnSpan;
    /**
     * Value of the option.
     */
    hasValue?: boolean;
    /**
     * Help source URL associated with the component.
     */
    helpSourceLink?: InlineUserAssistanceProps['helpSourceLink'];
    /**
     * Custom text to be rendered for the <code>helpSourceLink</code>.
     */
    helpSourceText?: InlineUserAssistanceProps['helpSourceText'];
    /**
     * Specifies if the set is disabled.
     */
    isDisabled?: boolean;
    /**
     * Specifies if the set is read-only.
     */
    isReadonly?: boolean;
    /**
     * Specifies whether the set requires a selection to be made.
     */
    isRequired?: boolean;
    /**
     * Label text for the set.
     */
    label: string;
    /**
     * Specifies where the label is positioned relative to the field.
     */
    labelEdge?: 'none' | 'top' | 'inside' | 'start';
    /**
     * Specifies the width of the label when <code>labelEdge</code> is <code>"start"</code>.
     */
    labelStartWidth?: Size;
    /**
     * Messages to show on screen that are associated with the component.
     */
    messages?: InlineUserAssistanceProps['messages'];
    /**
     * Specifies the parent field type.
     */
    parentComponentVariant: 'richRadioSet' | 'richCheckboxSet';
    /**
     * Specifies the ARIA role type.
     */
    role?: 'radiogroup' | 'group';
    /**
     * Specifies the density of the user assistance presentation.  It can be set to:
     * <ul>
     * <li><code>'reflow'</code>: Show inline. Layout will reflow when text is displayed.</li>
     * <li><code>'efficient'</code>: Show inline and reserve space to prevent layout reflow when user
     * assistance text is displayed.</li>
     * <li><code>'compact'</code>: Show inline and reserve space to prevent layout reflow when user
     * assistance text is displayed.</li>
     * </ul>
     */
    userAssistanceDensity?: UserAssistanceDensityType;
  };

/**
 * Internal component use by rich selection components to manage focus and layout labels and messages
 */
export const RichSelectionField = forwardRef(
  (
    {
      'aria-describedby': ariaDescribedBy,
      assistiveText,
      baseId,
      children,
      columnSpan = 1,
      hasValue,
      helpSourceLink,
      helpSourceText,
      isDisabled,
      isReadonly,
      isRequired,
      label,
      labelEdge,
      labelStartWidth = '33%',
      messages,
      parentComponentVariant,
      role,
      testId,
      userAssistanceDensity
    }: Props,
    ref: Ref<FocusableHandle> = null
  ) => {
    const testIdProps = useTestId(testId);
    const UAtestId = `${testId}-inline-user-assistance`;
    const UAtestIdTop = testId ? `${UAtestId}-top` : undefined;
    const UAtestIdBottom = testId ? `${UAtestId}-bottom` : undefined;
    const hasTopUserAssistance =
      !isDisabled && !isReadonly && (!!assistiveText || !!helpSourceLink);
    // we still render <InlineUserAssistance/> if density is 'efficient' or 'compact' to save space,
    // even though we don't render user assistance for disabled fields
    const hasBottomUserAssistance = !isDisabled;
    const groupRef = useRef<HTMLDivElement>(null);

    const {
      groupProps,
      labelProps,
      topUserAssistanceProps,
      bottomUserAssistanceProps,
      readonlyId
    } = useCheckboxRadioField({
      ariaDescribedBy,
      baseId,
      isRequired,
      isReadonly,
      isDisabled,
      label,
      labelEdge,
      messages,
      hasValue,
      hasTopUserAssistance,
      hasBottomUserAssistance,
      parentComponentVariant: parentComponentVariant === 'richRadioSet' ? 'radioSet' : 'checkboxSet'
    });

    const [startData, setStartData] = useState({
      hasMedia: false,
      hasSecondaryText: false
    });
    const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
    const readonlyLabel = translations.formControl_readOnly();

    useImperativeHandle(ref, () => ({
      focus: () => {
        if (!isDisabled) {
          const inputChildren = groupRef.current?.querySelectorAll<HTMLInputElement>('input') ?? [];
          const selectedChild = Array.from(inputChildren).find((child) => child.checked);

          if (selectedChild) {
            selectedChild.focus();
          } else {
            inputChildren[0].focus();
          }
        }
      },
      blur: () => {
        if (groupRef.current?.contains(document.activeElement)) {
          (document.activeElement as HTMLElement).blur();
        }
      }
    }));

    // the props from the hook are used for focus management on the radioset variant to allow for arrow navigation
    // while checkboxset uses tabbing
    const [isFocusRingShown, focusRingProps] = useCollectionFocusRing(
      groupRef,
      parentComponentVariant === 'richRadioSet'
        ? ['ArrowUp', 'ArrowRight', 'ArrowDown', 'ArrowLeft']
        : ['Tab']
    );

    const { gridTemplateColumns, ref: breakpointRef } = useRichSelectionGrid();

    const memoizedChildren = useMemo(() => {
      return toChildArray(children).map((child, index) => {
        // we only need to wrap children with starting labels in context because they are the only ones
        // that can conditionally apply min-height and set the startData
        return labelEdge === 'start' && !hasTopUserAssistance ? (
          // the first child is the one that will set the startData
          <RichSelectionStartContext.Provider value={{ setStartData, isFirst: index === 0 }}>
            {child}
          </RichSelectionStartContext.Provider>
        ) : (
          child
        );
      });
    }, [children, hasTopUserAssistance, labelEdge]);

    const content = (
      <>
        {hasTopUserAssistance && (
          <div
            id={`${baseId}-top-ua-container`}
            class={labelEdge === 'start' ? topUAContainerStyles.start : topUAContainerStyles.top}>
            <InlineUserAssistanceContainer {...topUserAssistanceProps} testId={UAtestIdTop}>
              <InlineHelp
                assistiveText={assistiveText}
                sourceLink={helpSourceLink}
                sourceText={helpSourceText}
              />
            </InlineUserAssistanceContainer>
          </div>
        )}
        <View padding={['2x', 0]} width="100%">
          <Grid gridTemplateColumns={gridTemplateColumns} gap="6x" role="list">
            <CheckboxRadioContext.Provider value={{ isFocusRingShown }}>
              {memoizedChildren}
            </CheckboxRadioContext.Provider>
          </Grid>
        </View>
        {hasBottomUserAssistance ? (
          <InlineUserAssistance
            {...bottomUserAssistanceProps}
            requiredAlignment="start"
            messages={messages}
            userAssistanceDensity={userAssistanceDensity === 'reflow' ? 'reflow' : 'efficient'}
            testId={UAtestIdBottom}
          />
        ) : userAssistanceDensity === 'reflow' ? undefined : (
          /* save space for user assistance if density is 'efficient' or 'compact', even though we don't
             render user assistance for disabled fields */
          <InlineUserAssistance
            id={bottomUserAssistanceProps?.id}
            userAssistanceDensity="efficient"
            testId={UAtestIdBottom}
          />
        )}
      </>
    );
    // The top level element needs the column span class on it.
    const classes = layoutSpanStyles.layoutSpanColumn[columnSpan];

    return (
      <div
        ref={mergeRefs(groupRef, breakpointRef)}
        class={classes}
        role={role}
        {...groupProps}
        {...focusRingProps}
        {...testIdProps}>
        {labelEdge === 'none' ? (
          content
        ) : (
          <RichLabelValueLayout
            columnSpan={columnSpan}
            parentComponentVariant={parentComponentVariant}
            hasTopUserAssistance={hasTopUserAssistance}
            hasSecondaryText={startData.hasSecondaryText}
            hasMedia={startData.hasMedia}
            label={
              <Label
                {...labelProps}
                variant={labelEdge}
                userAssistanceDensity="efficient"
                parentComponentVariant={parentComponentVariant}>
                {label}
              </Label>
            }
            labelEdge={labelEdge}
            labelStartWidth={labelStartWidth}>
            {content}
          </RichLabelValueLayout>
        )}
        {isReadonly && (
          <HiddenAccessible id={readonlyId} isHidden={true}>
            {readonlyLabel}
          </HiddenAccessible>
        )}
      </div>
    );
  }
);
